/**
 * Create / edit / view modal for personal and assigned post-its.
 */

import * as React from 'react';
import {
  PostIt,
  CreatePostItDto,
  UpdatePostItDto,
  PostItThematic,
  PostItColor,
  PostItStatus,
  AssignedRecurrence,
  CreateAssignedPostItDto,
  AssignedPostIt,
  ScheduleConfig,
  type PostItShare,
} from '@/types/post-it.types';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/forms/Button';
import { Input } from '@/components/forms/Input';
import { Textarea } from '@/components/forms/Textarea';
import { Select } from '@/components/forms/Select';
import { DateTimePicker } from '@/components/forms/DateTimePicker';
import { Star, Clock, Palette, Tag, Save, Plus, ShieldUser, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/toast';
import { useFormValidation } from '@/hooks/useFormValidation';
import { postItValidationSchema } from '@/utils/validation-schemas-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import '@/styles/postit.css';
import { THEMATICS, COLOR_MAP_MODAL } from './post-it-configs';
import {
  buildAssignedPayload,
  buildPersonalPayload,
  buildShareModalPlaceholder,
  buildTargetRoleOptions,
  getAvailablePostItColors,
  getModalSubmitError,
  hydratePostItModalState,
  isPostItSubmitDisabled,
  modalDialogTitle,
  normalizeDeadline,
  resolveExistingShares,
  type PendingPostItShare,
} from './post-it-form-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAllowedTargetRoles, getShares, removeShare } from '@/services/post-its.service';
import { useMode } from '@/contexts/ModeContext';
import { ScheduleConfigForm } from './planning/schedule/ScheduleConfigForm';
import { ShareModal } from './ShareModal';

/** Props for {@link PostItModal}. */
type PostItModalProps =
  | {
      variant: 'personal';
      mode: 'create' | 'edit' | 'view';
      postIt?: PostIt;
      onSave: (
        data: CreatePostItDto | UpdatePostItDto,
        pendingShare?: PendingPostItShare,
      ) => Promise<void>;
      isOpen: boolean;
      onClose: () => void;
    }
  | {
      variant: 'assigned';
      mode: 'create' | 'edit' | 'view';
      postIt?: PostIt;
      /** Prefill view/edit for an existing assigned post-it. */
      assignedPostIt?: AssignedPostIt;
      onSave: (data: CreateAssignedPostItDto) => Promise<void>;
      /** Save an existing assigned post-it in edit mode. */
      onUpdate?: (id: string, data: Partial<CreateAssignedPostItDto>) => Promise<void>;
      isOpen: boolean;
      onClose: () => void;
    };

/** Create / edit / view modal for personal and assigned post-its. */
export const PostItModal: React.FC<PostItModalProps> = (props) => {
  const { variant, isOpen, onClose } = props;
  const queryClient = useQueryClient();
  const postIt = props.postIt;
  const assignedPostIt =
    variant === 'assigned'
      ? (props as { assignedPostIt?: AssignedPostIt }).assignedPostIt
      : undefined;
  // Allow switching from view → edit inline
  const [currentMode, setCurrentMode] = React.useState<'create' | 'edit' | 'view'>(props.mode);

  const [formData, setFormData] = React.useState({
    title: postIt?.title || '',
    thematic: postIt?.thematic || '',
    content: postIt?.content || '',
    color: postIt?.color || ((variant === 'assigned' ? 'blanc-casse' : 'jaune') as PostItColor),
    isPriority: postIt?.isPriority || false,
  });

  const [personalData, setPersonalData] = React.useState({
    deadline: normalizeDeadline(postIt?.deadline),
    status: (postIt?.status || 'en_cours') as PostItStatus,
  });

  const [shareModalOpen, setShareModalOpen] = React.useState(false);
  const [pendingShare, setPendingShare] = React.useState<PendingPostItShare | null>(null);

  const [assignConfig, setAssignConfig] = React.useState({
    recurrence: '' as AssignedRecurrence | '',
    specificDateFrom: '',
    specificDateTo: '',
    targetRoleIds: [] as string[],
    quarterlyPeriods: [] as number[],
  });

  const [scheduleConfig, setScheduleConfig] = React.useState<ScheduleConfig | null>(null);

  const [loading, setLoading] = React.useState(false);
  const validation = useFormValidation(postItValidationSchema);

  React.useEffect(() => {
    if (!isOpen) return;
    setCurrentMode(props.mode);

    const hydrated = hydratePostItModalState(variant, postIt, assignedPostIt);
    setFormData(hydrated.formData);

    if (hydrated.personalData) {
      setPersonalData(hydrated.personalData);
      setPendingShare(null);
      setShareModalOpen(false);
      if (hydrated.shouldValidateInitial) {
        validation.validateInitialFormData({ ...hydrated.formData, ...hydrated.personalData });
      } else {
        validation.resetValidation();
      }
    } else {
      if (hydrated.assignConfig) {
        setAssignConfig(hydrated.assignConfig);
      }
      if (hydrated.scheduleConfig) {
        setScheduleConfig(hydrated.scheduleConfig);
      }
      validation.resetValidation();
    }
  }, [postIt, assignedPostIt, isOpen]);

  const { mode: appMode } = useMode();

  const { data: allowedRoles } = useQuery({
    queryKey: ['allowed-target-roles', appMode],
    queryFn: () => getAllowedTargetRoles(appMode ?? undefined),
    enabled: isOpen && variant === 'assigned' && !!appMode,
  });

  const roleOptions = React.useMemo(
    () => buildTargetRoleOptions(allowedRoles, assignConfig.targetRoleIds[0]),
    [allowedRoles, assignConfig.targetRoleIds],
  );

  /** Patch one form field and run field-level validation. */
  const handleFieldChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      validation.validateField(field, value, newData);
      return newData;
    });
  };

  const handleBlur = (field: keyof typeof formData) => {
    validation.validateField(field, formData[field], formData);
  };

  const handleAssignChange = (
    field: keyof typeof assignConfig,
    value: string | string[] | number[] | undefined,
  ) => {
    setAssignConfig((prev) => ({ ...prev, [field]: value }));
  };

  const isSubmitDisabled = () =>
    isPostItSubmitDisabled(formData, variant, loading, scheduleConfig, assignConfig);

  const availableColors = React.useMemo(
    () => getAvailablePostItColors(currentMode, formData.color),
    [currentMode, formData.color],
  );

  const persistPostIt = async () => {
    const dataToValidate =
      variant === 'personal' ? { ...formData, deadline: personalData.deadline } : formData;
    const isValid = validation.validateForm(dataToValidate);
    const submitError = getModalSubmitError(
      variant,
      formData,
      personalData,
      scheduleConfig,
      assignConfig,
      isValid,
    );
    if (submitError) {
      toast.error(getErrorMessage(submitError));
      return;
    }

    const saveAssigned = async () => {
      const data = buildAssignedPayload(formData, assignConfig, scheduleConfig);
      if (currentMode === 'edit' && assignedPostIt) {
        const onUpdate = (
          props as {
            onUpdate?: (id: string, d: Partial<CreateAssignedPostItDto>) => Promise<void>;
          }
        ).onUpdate;
        if (onUpdate) {
          await onUpdate(assignedPostIt.id, data);
        }
        return;
      }
      await (props.onSave as (d: CreateAssignedPostItDto) => Promise<void>)(data);
    };

    const savePersonal = async () => {
      const data = buildPersonalPayload(formData, personalData, currentMode);
      await (
        props.onSave as (
          d: CreatePostItDto | UpdatePostItDto,
          share?: PendingPostItShare,
        ) => Promise<void>
      )(data, pendingShare ?? undefined);
    };

    setLoading(true);
    try {
      if (variant === 'assigned') {
        await saveAssigned();
      } else {
        await savePersonal();
      }
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    void persistPostIt();
  };

  const isReadOnly = currentMode === 'view';
  const colorStyles = COLOR_MAP_MODAL[formData.color];
  const currentThematic = THEMATICS.find((t) => t.value === formData.thematic);

  const shareModalPostIt: PostIt = React.useMemo(
    () => postIt ?? buildShareModalPlaceholder(formData),
    [postIt, formData],
  );

  // List payload omits shares — load them when opening share picker on edit
  const { data: sharesResponse } = useQuery({
    queryKey: ['postit-shares', postIt?.id],
    queryFn: () => getShares(postIt!.id),
    enabled: shareModalOpen && Boolean(postIt?.id),
  });

  const existingShares: PostItShare[] = React.useMemo(
    () => resolveExistingShares(sharesResponse, postIt?.shares),
    [sharesResponse, postIt?.shares],
  );

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open && !shareModalOpen) onClose();
        }}
      >
        <DialogContent
          className={cn(
            'max-w-lg p-0 border-2 overflow-hidden',
            colorStyles.bg,
            colorStyles.border,
            'shadow-2xl',
            'postit-modal-tilt',
            'max-h-[90vh] flex flex-col',
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Visually hidden accessible title — required by Radix UI */}
          <DialogTitle className="sr-only">
            {modalDialogTitle(Boolean(assignedPostIt || postIt), variant)}
          </DialogTitle>
          <div className={cn('relative p-5 pb-3', colorStyles.text)}>
            {isReadOnly && (
              <div className="absolute top-3 left-3 text-2xl">{currentThematic?.emoji || '📌'}</div>
            )}
            {formData.isPriority && (
              <div className={cn('absolute top-3', isReadOnly ? 'left-12' : 'left-3')}>
                <Star className="w-5 h-5 fill-[#f59e0b] text-[#f59e0b]" />
              </div>
            )}
            <div className="mt-8">
              <Input
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                onBlur={() => handleBlur('title')}
                placeholder="Titre du post-it..."
                required
                disabled={isReadOnly}
                className={cn(
                  '!bg-transparent border-none outline-none shadow-none',
                  'font-caveat text-2xl font-bold placeholder:opacity-30 placeholder:font-light',
                  colorStyles.text,
                  'disabled:opacity-70',
                  'focus-visible:ring-0 focus-visible:ring-offset-0',
                  validation.getFieldError('title') && 'border-b-2 border-red-500',
                )}
                maxLength={255}
                error={validation.getFieldError('title')}
              />
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className={cn('px-5 pb-5 overflow-y-auto flex-1', colorStyles.text)}
          >
            <Textarea
              value={formData.content}
              onChange={(e) => handleFieldChange('content', e.target.value)}
              onBlur={() => handleBlur('content')}
              placeholder="Écrivez votre note ici..."
              required
              disabled={isReadOnly}
              className={cn(
                'bg-transparent border-none outline-none shadow-none resize-none',
                'font-caveat text-lg placeholder:opacity-30 placeholder:font-light',
                colorStyles.text,
                'disabled:opacity-70',
                'focus-visible:ring-0 focus-visible:ring-offset-0',
                validation.getFieldError('content') && 'border-2 border-red-500 rounded-lg p-2',
              )}
              rows={variant === 'assigned' ? 4 : 5}
              maxLength={1000}
              error={validation.getFieldError('content')}
            />

            <div className="mt-4 pt-4 border-t border-black/10 space-y-4">
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[140px]">
                  <Tag className="w-4 h-4 opacity-60 flex-shrink-0" />
                  <Select
                    value={formData.thematic}
                    onChange={(value) => handleFieldChange('thematic', value as PostItThematic)}
                    onBlur={() => handleBlur('thematic')}
                    disabled={isReadOnly}
                    placeholder="Thématique"
                    options={THEMATICS.map((t) => ({
                      value: t.value,
                      label: `${t.emoji} ${t.label}`,
                    }))}
                    className={cn('flex-1', colorStyles.text)}
                    size="sm"
                  />
                </div>

                {variant === 'personal' && (
                  <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <Clock className="w-5 h-5 opacity-60 flex-shrink-0" />
                    <DateTimePicker
                      value={personalData.deadline}
                      onChange={(value) => {
                        setPersonalData((prev) => ({ ...prev, deadline: value }));
                        validation.validateField('deadline', value, {
                          ...formData,
                          deadline: value,
                        });
                      }}
                      disabled={isReadOnly}
                      className="flex-1"
                      disablePast={true}
                      timeStep={5}
                      minTime="08:00"
                      maxTime="18:00"
                      error={validation.getFieldError('deadline')}
                    />
                  </div>
                )}

                {variant === 'assigned' && (
                  <div className="flex items-center gap-2 flex-1 min-w-[160px]">
                    <ShieldUser className="w-4 h-4 opacity-60 flex-shrink-0" />
                    <Select
                      placeholder="Choisir un rôle"
                      options={roleOptions}
                      value={assignConfig.targetRoleIds[0] ?? ''}
                      onChange={(val) => handleAssignChange('targetRoleIds', val ? [val] : [])}
                      disabled={isReadOnly}
                      className={cn('flex-1', colorStyles.text)}
                      size="sm"
                    />
                  </div>
                )}
              </div>

              {variant === 'assigned' && (
                <div className="postit-modal__schedule-section">
                  <ScheduleConfigForm
                    value={scheduleConfig}
                    onChange={(config) => setScheduleConfig(config)}
                    templateId={assignedPostIt?.id}
                    color={formData.color}
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 opacity-60" />
                <div className="flex gap-1.5">
                  {availableColors.map((c) => (
                    <Button
                      key={c}
                      type="button"
                      preset="postit-color"
                      onClick={() => !isReadOnly && handleFieldChange('color', c)}
                      disabled={isReadOnly}
                      className={cn(
                        COLOR_MAP_MODAL[c].bg,
                        COLOR_MAP_MODAL[c].hoverBg,
                        formData.color === c
                          ? 'border-black/40 scale-110 shadow-md'
                          : 'border-black/10 hover:scale-105',
                        'disabled:cursor-not-allowed',
                      )}
                      title={
                        c === 'blanc-casse'
                          ? 'Blanc cassé (post-it planifié)'
                          : c.charAt(0).toUpperCase() + c.slice(1)
                      }
                    />
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    preset="postit-action"
                    icon={Star}
                    onClick={() =>
                      !isReadOnly && handleFieldChange('isPriority', !formData.isPriority)
                    }
                    disabled={isReadOnly}
                    aria-label="Marquer comme prioritaire"
                    className={cn(
                      formData.isPriority
                        ? 'bg-[#f59e0b]/20 [&_svg]:fill-[#f59e0b] [&_svg]:text-[#f59e0b]'
                        : '[&_svg]:text-black/40',
                    )}
                  />
                  <span className="text-sm opacity-70">Marquer comme prioritaire</span>
                </div>

                {variant === 'personal' && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      preset="postit-action"
                      icon={Share2}
                      onClick={() => !isReadOnly && setShareModalOpen(true)}
                      disabled={isReadOnly}
                      aria-label="Partager avec"
                      className={cn(
                        pendingShare && pendingShare.userIds.length > 0
                          ? 'bg-[var(--color-2-nuance-1)]/20 [&_svg]:text-[var(--color-2-nuance-1)]'
                          : '[&_svg]:text-black/40',
                      )}
                    />
                    <button
                      type="button"
                      disabled={isReadOnly}
                      onClick={() => !isReadOnly && setShareModalOpen(true)}
                      className="text-sm opacity-70 hover:opacity-100 disabled:cursor-not-allowed text-left"
                    >
                      {pendingShare && pendingShare.userIds.length > 0
                        ? `Partager avec (${pendingShare.userIds.length})`
                        : 'Partager avec'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {!isReadOnly && (
              <div className="mt-5 flex justify-end">
                <Button
                  preset="form-save"
                  iconOnly
                  icon={currentMode === 'create' ? Plus : Save}
                  loading={loading}
                  disabled={isSubmitDisabled()}
                  aria-label={currentMode === 'create' ? 'Créer' : 'Enregistrer'}
                />
              </div>
            )}
            {isReadOnly && variant === 'assigned' && (
              <div className="mt-5 flex justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentMode('edit')}
                >
                  Modifier
                </Button>
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {variant === 'personal' && shareModalOpen && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          postIt={shareModalPostIt}
          deferUntilSave
          existingShares={existingShares}
          initialSelection={pendingShare ?? undefined}
          onShareUsers={(userIds, permission) => {
            const alreadyIds = new Set(existingShares.map((s) => s.userId));
            const unique = [...new Set(userIds)].filter((id) => !alreadyIds.has(id));
            setPendingShare(unique.length > 0 ? { userIds: unique, permission } : null);
            return Promise.resolve();
          }}
          onShareRole={() => Promise.resolve()}
          onRemoveShare={async (userId) => {
            if (!postIt?.id) return;
            await removeShare(postIt.id, userId);
            await queryClient.invalidateQueries({ queryKey: ['postit-shares', postIt.id] });
            await queryClient.invalidateQueries({ queryKey: ['postits'] });
          }}
        />
      )}
    </>
  );
};
